import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { passwordStrengthValidator } from '../../../shared/validators/custom-validators';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { USER_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

function confirmPasswordValidator(control: AbstractControl): ValidationErrors | null {
  const parent = control.parent;
  if (!parent) return null;
  const password = parent.get('password')?.value;
  return password === control.value ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-user-master',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './user-master.component.html',
  styleUrl: './user-master.component.scss',
})
export class UserMasterComponent implements OnInit, OnDestroy {
  view: 'list' | 'form' = 'list';

  // Auth
  isAdmin = false;

  // List state
  records: any[] = [];
  isListLoading = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';
  showPassword = false;
  showConfirm = false;
  changePassword = false;

  // Center assignment (manager role)
  allCenters: any[] = [];
  selectedCenterIds = new Set<string>();
  isCentersLoading = false;
  private roleSub?: Subscription;

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  roles = ['admin', 'manager', 'runner'];

  tableConfig: VT2_TABLE_CONFIG = {
    ...USER_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
    ],
  };

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private auth: AuthService,
    private pageTitle: PageTitleService,
    private dateFmt: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.isAdmin = this.auth.getCurrentUser()?.role?.toLowerCase() === 'admin';
    this.pageTitle.set('User Master', 'Manage system users');
    this.buildForm();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.roleSub?.unsubscribe();
  }

  buildForm(): void {
    this.form = this.fb.group({
      name:            ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email:           ['', [Validators.required, Validators.email]],
      password:        ['', [Validators.required, Validators.minLength(8), passwordStrengthValidator()]],
      confirmPassword: ['', [Validators.required, confirmPasswordValidator]],
      role:            ['runner', [Validators.required]],
    });

    this.roleSub = this.form.get('role')!.valueChanges.subscribe(role => {
      if (role === 'manager') {
        this.loadAllCenters();
      } else {
        this.allCenters = [];
        this.selectedCenterIds.clear();
      }
    });
  }

  private loadAllCenters(): void {
    this.isCentersLoading = true;
    this.api.getCenters().subscribe({
      next: (centers) => { this.allCenters = centers || []; this.isCentersLoading = false; },
      error: () => { this.isCentersLoading = false; },
    });
  }

  toggleCenter(centerId: string, event: Event): void {
    (event.target as HTMLInputElement).checked
      ? this.selectedCenterIds.add(centerId)
      : this.selectedCenterIds.delete(centerId);
  }

  private saveManagerCenters(managerId: string): void {
    this.api.assignCentersToManager(managerId, [...this.selectedCenterIds]).subscribe({ error: () => {} });
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getUsers().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.changePassword = false;
    this.showPassword = false;
    this.showConfirm = false;
    this.allCenters = [];
    this.selectedCenterIds.clear();
    this.form.reset({ role: 'runner' });
    this.setPasswordValidators(true);
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('User Master', 'Create a new system user account');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.changePassword = false;
    this.showPassword = false;
    this.showConfirm = false;
    this.allCenters = [];
    this.selectedCenterIds.clear();
    this.successMessage = '';
    this.errorMessage = '';
    this.setPasswordValidators(false);
    this.form.patchValue({
      name:  record.name,
      email: record.email,
      role:  record.role,
    });
    this.form.get('password')?.setValue('');
    this.form.get('confirmPassword')?.setValue('');
    // Load centers for manager role
    if (record.role === 'manager') {
      this.loadAllCenters();
      this.api.getCentersByManager(record._id || record.id).subscribe({
        next: (centers) => {
          this.selectedCenterIds = new Set((centers || []).map((c: any) => c._id || c.id));
        },
        error: () => {},
      });
    }
    this.view = 'form';
    this.pageTitle.set('User Master', 'Update system user details');
  }

  backToList(): void {
    this.view = 'list';
    this.pageTitle.set('User Master', 'Manage system users');
    this.loadList();
  }

  private setPasswordValidators(required: boolean): void {
    if (required) {
      this.form.get('password')?.setValidators([Validators.required, Validators.minLength(8), passwordStrengthValidator()]);
      this.form.get('confirmPassword')?.setValidators([Validators.required, confirmPasswordValidator]);
    } else {
      this.form.get('password')?.clearValidators();
      this.form.get('confirmPassword')?.clearValidators();
    }
    this.form.get('password')?.updateValueAndValidity();
    this.form.get('confirmPassword')?.updateValueAndValidity();
  }

  toggleChangePassword(): void {
    this.changePassword = !this.changePassword;
    this.setPasswordValidators(this.changePassword);
    if (!this.changePassword) {
      this.form.get('password')?.setValue('');
      this.form.get('confirmPassword')?.setValue('');
    }
  }

  get f() { return this.form.controls; }

  private get pwd(): string { return this.f['password'].value || ''; }
  get pwdHas8(): boolean      { return this.pwd.length >= 8; }
  get pwdHasUpper(): boolean  { return /[A-Z]/.test(this.pwd); }
  get pwdHasLower(): boolean  { return /[a-z]/.test(this.pwd); }
  get pwdHasNumber(): boolean { return /[0-9]/.test(this.pwd); }
  get pwdHasSpecial(): boolean { return /[@$!%*?&#^()_+\-=]/.test(this.pwd); }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteUser(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete user.';
      },
    });
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const { confirmPassword, role, ...rest } = this.form.value;
    const payload: any = { ...rest, userRole: role };
    if (this.isEditMode && !this.changePassword) {
      delete payload.password;
    }

    const request$ = this.isEditMode
      ? this.api.updateUser({ ...payload, id: this.editId })
      : this.api.saveUser(payload);

    request$.subscribe({
      next: (result: any) => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'User updated successfully!' : 'User saved successfully!';
        // Save center assignments for manager role
        const isManager = this.form.get('role')?.value === 'manager';
        if (isManager) {
          const managerId = this.isEditMode ? this.editId! : (result?.userId || '');
          if (managerId) this.saveManagerCenters(managerId);
        }
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save user.';
      },
    });
  }
}
